import { useMemo } from 'react';
import { BooleanData, Category, Date as DateIcon, DateRange, DecimalType, Filter, NestedGroup, NumericField, TextField } from '@sas/react-svg';
import { createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../../locale/formatting';
import { byReferenceEquality, inCommon } from '../borrowed/catalog-app/typescript/src/app/util/array';
import { sortArray } from '../borrowed/catalog-app/typescript/src/app/util/sort';
import { NESTED_FACET_CURSOR_OFFSET, NESTED_FACET_SYNTAX_SUFFIX_LABEL, NESTED_FACET_SYNTAX_SUFFIX_VALUE, PREVIOUS_DAY_SEARCH_VALUE, SEARCH_FACET_END_TOKEN } from '../shared';
import { useCatalogPreviousSearches } from '../useCatalogPreviousSearches/useCatalogPreviousSearches';
import { useCatalogSearchFacets } from '../useCatalogSearchFacets/useCatalogSearchFacets';
import { useCatalogSearchHelp } from '../useCatalogSearchHelp/useCatalogSearchHelp';
import { useCatalogSearchParser } from '../useCatalogSearchParser/useCatalogSearchParser';
import { useCatalogSuggestions } from '../useCatalogSuggestions/useCatalogSuggestions';
import bundle from '../../NovaComponents-CatalogSearch-gui-icu.properties';
const useTextLocalizer = createUseTextLocalizer(bundle);
export const useCatalogSuggestionsData = ({
  enableExperimental,
  currentText,
  currentSearchIndices,
  selectionStartPosition = -1,
  selectionEndPosition = -1
}) => {
  const localize = useTextLocalizer();
  const formatLocale = useFormatLocale();
  const {
    data: allSearchFacets
  } = useCatalogSearchFacets();
  const visibleFacetsForCurrentIndices = useMemo(() => {
    if (allSearchFacets) {
      if (!currentSearchIndices || currentSearchIndices.length === 0) {
        // If the user hasn't applied any index filters, then we grab all facets that are visible in
        // at least one of our indices.
        const facetsVisibleInAtLeastOneIndex = {};
        for (const facetInfo of Object.values(allSearchFacets)) {
          if (facetInfo.indices && facetInfo.indices.length > 0) {
            facetsVisibleInAtLeastOneIndex[facetInfo.name.toLocaleLowerCase()] = facetInfo;
          }
        }
        return facetsVisibleInAtLeastOneIndex;
      }
      const filteredFacets = {};
      for (const facetInfo of Object.values(allSearchFacets)) {
        if (facetInfo.indices && inCommon(facetInfo.indices, currentSearchIndices, byReferenceEquality).length > 0) {
          filteredFacets[facetInfo.name.toLocaleLowerCase()] = facetInfo;
        }
      }
      return filteredFacets;
    }
    return undefined;
  }, [allSearchFacets, currentSearchIndices]);
  const parseInfo = useCatalogSearchParser({
    currentText,
    selectionStartPosition,
    selectionEndPosition,
    facetsForCurrentIndices: allSearchFacets
  });
  const suggestionHelp = useCatalogSearchHelp({
    facetInfo: parseInfo?.facetInfo || null,
    enableExperimental
  });
  const {
    data: previousSearches
  } = useCatalogPreviousSearches();
  const filteredPrevious = useMemo(() => {
    if (currentText === undefined || !previousSearches) return [];
    const inputLower = currentText.toLocaleLowerCase();
    return previousSearches.filter(ps => {
      const previousLower = ps.query?.toLocaleLowerCase();
      // For previous searches, do startsWith. Otherwise it will match if they're searching on the same facet.
      return previousLower && previousLower.startsWith(inputLower);
    });
  }, [previousSearches, currentText]);
  const {
    data: typeAheadSuggestions
  } = useCatalogSuggestions({
    facetInfo: parseInfo?.facetInfo || undefined,
    initialQuery: parseInfo?.value,
    indices: currentSearchIndices,
    limit: parseInfo?.facetInfo?.valueType === 'text' && (parseInfo?.value === undefined || parseInfo?.value === '') ? 20 : 100
  });
  const allDateSuggestions = useMemo(() => {
    const dateSuggestionValues = [{
      label: localize('search.dateFilter.previousday.label'),
      value: PREVIOUS_DAY_SEARCH_VALUE
    }, {
      label: localize('search.dateFilter.previousweek.label'),
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      value: 'Previous week'
    }, {
      label: localize('search.dateFilter.previousmonth.label'),
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      value: 'Previous month'
    }, {
      label: localize('search.dateFilter.previousquarter.label'),
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      value: 'Previous quarter'
    }, {
      label: localize('search.dateFilter.previousyear.label'),
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      value: 'Previous year'
    }];
    return dateSuggestionValues;
  }, [localize]);
  const facetOrValueSuggestions = useMemo(() => {
    let fvSuggestions = [];
    if (parseInfo?.suggestionType === 'value') {
      const valueSuggestions = getPlainSuggestionsForFacetType({
        localize,
        formatLocale,
        allDateSuggestions,
        typeAheadSuggestions: typeAheadSuggestions || undefined,
        suggestionParseInfo: parseInfo
      });
      fvSuggestions = valueSuggestions;
    } else if (parseInfo?.suggestionType === 'facet') {
      const currentFacetEntry = parseInfo?.facetInfo?.name ?? parseInfo?.parsedFacet;
      const facets = getFacetSuggestions({
        allFacets: visibleFacetsForCurrentIndices,
        facetNameFilter: currentFacetEntry,
        currentText
      });
      const facetSuggestions = facets ? Object.values(facets).map(fi => {
        let syntaxAddition = '';
        let syntaxAdditionValue = '';
        let cursorOffset = 0;
        if (fi.valueType === 'nested') {
          syntaxAddition = NESTED_FACET_SYNTAX_SUFFIX_LABEL;
          syntaxAdditionValue = NESTED_FACET_SYNTAX_SUFFIX_VALUE;
          cursorOffset = NESTED_FACET_CURSOR_OFFSET;
        }
        let facetIcon = Filter;
        let facetIconTooltip = fi.label || localize('search.filter.icon.tooltip.label');
        if (fi.valueType === 'date') {
          facetIcon = DateIcon;
          facetIconTooltip = fi.label || localize('search.date.icon.tooltip.label');
        } else if (fi.valueType === 'keyword') {
          facetIcon = Category;
          facetIconTooltip = fi.label || localize('search.category.icon.tooltip.label');
        } else if (fi.valueType === 'text') {
          facetIcon = TextField;
          facetIconTooltip = fi.label || localize('search.textfield.icon.tooltip.label');
        } else if (fi.valueType === 'nested') {
          facetIcon = NestedGroup;
          facetIconTooltip = fi.label || localize('search.nestedgroup.icon.tooltip.label');
        } else if (fi.valueType === 'integer') {
          facetIcon = NumericField;
          facetIconTooltip = fi.label || localize('search.numericfield.icon.tooltip.label');
        } else if (fi.valueType === 'long') {
          facetIcon = NumericField;
          facetIconTooltip = fi.label || localize('search.numericfield.icon.tooltip.label');
        } else if (fi.valueType === 'float') {
          facetIcon = DecimalType;
          facetIconTooltip = fi.label || localize('search.decimaltype.icon.tooltip.label');
        } else if (fi.valueType === 'boolean') {
          facetIcon = BooleanData;
          facetIconTooltip = fi.label || localize('search.booleandata.icon.tooltip.label');
        }
        return {
          label: fi.name + SEARCH_FACET_END_TOKEN + syntaxAddition,
          value: fi.name + SEARCH_FACET_END_TOKEN + syntaxAdditionValue,
          icon: facetIcon,
          iconTooltip: facetIconTooltip,
          cursorOffset
        };
      }) : [];
      fvSuggestions = facetSuggestions;
    }
    return fvSuggestions;
  }, [allDateSuggestions, currentText, formatLocale, localize, parseInfo, typeAheadSuggestions, visibleFacetsForCurrentIndices]);
  const result = useMemo(() => {
    return {
      facetOrValueSuggestions,
      help: suggestionHelp,
      history: filteredPrevious,
      parseInfo
    };
  }, [facetOrValueSuggestions, filteredPrevious, parseInfo, suggestionHelp]);
  return result;
};
const getFacetSuggestions = props => {
  if (props.currentText === undefined) {
    return null;
  }
  if (props.allFacets !== undefined) {
    // Suggest facets
    if (props.facetNameFilter !== undefined && props.facetNameFilter.length > 0) {
      const filter = props.facetNameFilter.toLocaleLowerCase().trim();
      // TODO: should we be filtering with contains(indexOf) or startsWith?
      const facetNames = Object.keys(props.allFacets);
      const matchingFacets = {};
      for (const facetName of facetNames) {
        const facetInfo = props.allFacets[facetName];
        if (facetName.toLocaleLowerCase().indexOf(filter) > -1 || (!!facetInfo.label ? facetInfo.label.toLocaleLowerCase().indexOf(filter) > -1 : false)) {
          matchingFacets[facetInfo.name.toLocaleLowerCase()] = facetInfo;
        }
      }
      return matchingFacets;
    }
    if (props.currentText.length > 0) {
      // There's text entered, but no text entered for a current facet. Return all facets.
      return props.allFacets;
    }
    const matchingFacets = {};
    // Note: the keys in the Record<string, FacetInfo> are stored lowercase!
    const zeroStateFacets = ['name', 'datemodified', 'datecreated', 'status', 'library.name'];
    for (const facetName of zeroStateFacets) {
      if (props.allFacets[facetName]) {
        matchingFacets[facetName] = props.allFacets[facetName];
      }
    }
    return matchingFacets;
  }
  return null;
};
const getPlainSuggestionsForFacetType = props => {
  const filterLower = props.suggestionParseInfo.value ? props.suggestionParseInfo.value.toLocaleLowerCase().trim() : '';
  const suggestionItems = [];
  if (!props.suggestionParseInfo.facetInfo) {
    // SOPHIA TODO: We should do our best to catch it before it gets here
    // console.error('An unknown facet was passed to getSuggestionsForFacetType, facet=' + facet);
    return suggestionItems;
  } else if (props.suggestionParseInfo.facetInfo.valueType === 'nested' || props.suggestionParseInfo.facetInfo.valueType === 'long' || props.suggestionParseInfo.facetInfo.valueType === 'integer' || props.suggestionParseInfo.facetInfo.valueType === 'float') {
    // We don't currently support suggestions on nested or numeric items. Future TODO.
    return suggestionItems;
  }
  if (props.suggestionParseInfo.facetInfo.valueType === 'date') {
    for (const ds of props.allDateSuggestions) {
      if (!props.suggestionParseInfo.value || ds.value.toLocaleLowerCase().indexOf(filterLower) > -1 || ds.label.toLocaleLowerCase().indexOf(filterLower) > -1) {
        suggestionItems.push({
          label: ds.label,
          value: ds.value
        });
      }
    }
    // Always include the custom range option at the end, regardless of filter.
    suggestionItems.push({
      label: props.localize('search.dateFilter.CustomRange.label'),
      value: null,
      icon: DateRange
    });
    return suggestionItems;
  }
  if (props.typeAheadSuggestions?.suggestions) {
    for (const sf of props.typeAheadSuggestions.suggestions) {
      const suggestionFacetNameLower = sf.toLocaleLowerCase();
      if (!filterLower || suggestionFacetNameLower.localeCompare(filterLower) !== 0 && suggestionFacetNameLower.startsWith(filterLower)) {
        let displayValue = sf;
        if (props.suggestionParseInfo.facetInfo?.name === 'reviewStatus') {
          // special handling for how we display the review status values
          displayValue = props.localize(`status.menuitem.${sf}.label`);
          // Make sure the localization was successful
          if (displayValue.startsWith('NovaComponents')) {
            displayValue = sf;
          }
        } else if (props.suggestionParseInfo.facetInfo?.name === 'informationPrivacy') {
          // special handling for how we display the information privacy values
          displayValue = props.localize(`informationPolicy.${sf}.msg`);
          // Make sure the localization was successful
          if (displayValue.startsWith('NovaComponents')) {
            displayValue = sf;
          }
        } else if (props.enableExperimental && props.suggestionParseInfo.facetInfo?.name === 'dataLocale') {
          displayValue = props.localize(`locales.country.language.${sf}.label`);
          // Make sure the localization was successful
          if (displayValue.startsWith('NovaComponents')) {
            displayValue = sf;
          }
        }
        suggestionItems.push({
          label: displayValue,
          value: sf
        });
      }
    }
  }
  sortArray({
    array: suggestionItems,
    propertyName: 'text',
    descending: false,
    formatLocale: props.formatLocale
  });
  return suggestionItems;
};
//# sourceMappingURL=useCatalogSuggestionsData.js.map